import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Check, Building2, AlertCircle } from 'lucide-react';
import { supabase, getInviteByToken, buildCurrentUser } from '../lib/supabase';
import axios from 'axios';
import { API_URL } from '../App';
import './AuthPages.css';
import './InvitePage.css';

const InvitePage = ({ currentUser, onLogin }) => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auth form state (for guests who aren't logged in)
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const [accepted, setAccepted] = useState(false);

  // Load invite details
  useEffect(() => {
    const loadInvite = async () => {
      try {
        const inv = await getInviteByToken(token);
        setInvite(inv);
        if (inv.email) setEmail(inv.email);
      } catch {
        setError('This invite link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    loadInvite();
  }, [token]);

  const acceptInvite = async (accessToken) => {
    await axios.post(
      `${API_URL}/api/org/invite/${token}/accept`,
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    setAccepted(true);
  };

  // If already logged in, show "Accept Invite" button directly
  const handleAcceptAsCurrentUser = async () => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const { data } = await supabase.auth.getSession();
      await acceptInvite(data.session.access_token);
      // Refresh user profile to pick up new org
      const updatedUser = await buildCurrentUser(data.session.user);
      await onLogin(updatedUser, data.session.access_token);
    } catch (err) {
      setAuthError(err.response?.data?.error || err.message || 'Failed to accept invite.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      let session;
      if (mode === 'login') {
        const { data, error: authErr } = await supabase.auth.signInWithPassword({ email, password });
        if (authErr) throw authErr;
        session = data.session;
      } else {
        const { data, error: authErr } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name } },
        });
        if (authErr) throw authErr;
        session = data.session;
      }

      await acceptInvite(session.access_token);

      const updatedUser = await buildCurrentUser(session.user);
      await onLogin(updatedUser, session.access_token);
    } catch (err) {
      setAuthError(err.response?.data?.error || err.message || 'Something went wrong.');
    } finally {
      setAuthLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="invite-container">
        <div className="invite-card loading-state">
          <div className="spinner" />
          <p>Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-container">
        <div className="invite-card error-state">
          <AlertCircle size={40} className="error-icon" />
          <h2>Invite Unavailable</h2>
          <p>{error}</p>
          <Link to="/login" className="btn-primary-onboarding">Go to Login</Link>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="invite-container">
        <div className="invite-card success-state">
          <div className="done-icon-sm">
            <Check size={32} />
          </div>
          <h2>You're in!</h2>
          <p>
            You've joined <strong>{invite?.organizations?.name}</strong> as a{' '}
            <strong>{invite?.role}</strong>.
          </p>
          <button className="btn-primary-onboarding" onClick={() => navigate('/home')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const orgName = invite?.organizations?.name || 'an organization';
  const orgType = invite?.organizations?.type || '';
  const roleName = invite?.role || 'member';

  return (
    <div className="invite-container">
      <div className="invite-card">
        {/* Org info */}
        <div className="invite-org-header">
          <div className="invite-org-icon">
            <Building2 size={28} />
          </div>
          <div className="invite-org-info">
            <h2 className="invite-org-name">{orgName}</h2>
            {orgType && <span className="invite-org-type">{orgType.replace(/_/g, ' ')}</span>}
          </div>
        </div>

        <p className="invite-description">
          You've been invited to join <strong>{orgName}</strong> on DealNexa as a{' '}
          <strong>{roleName}</strong>.
        </p>

        {authError && <div className="onboarding-error">{authError}</div>}

        {/* Already logged in */}
        {currentUser ? (
          <div className="invite-logged-in">
            <p className="invite-logged-in-note">
              Logged in as <strong>{currentUser.email}</strong>
            </p>
            <button
              className="btn-primary-onboarding"
              onClick={handleAcceptAsCurrentUser}
              disabled={authLoading}
            >
              {authLoading ? 'Joining...' : `Accept & Join ${orgName}`}
            </button>
            <p className="invite-wrong-account">
              Wrong account?{' '}
              <button
                className="link-btn"
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.reload();
                }}
              >
                Sign out
              </button>
            </p>
          </div>
        ) : (
          /* Auth form for guests */
          <div className="invite-auth">
            <div className="auth-tabs">
              <button
                className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
                onClick={() => setMode('login')}
              >
                Log In
              </button>
              <button
                className={`auth-tab ${mode === 'signup' ? 'active' : ''}`}
                onClick={() => setMode('signup')}
              >
                Create Account
              </button>
            </div>

            <form onSubmit={handleAuthSubmit} className="auth-form invite-auth-form">
              {mode === 'signup' && (
                <div className="auth-field">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    required
                  />
                </div>
              )}
              <div className="auth-field">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>
              <div className="auth-field">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
                  required
                  minLength={mode === 'signup' ? 8 : undefined}
                />
              </div>
              <button type="submit" className="auth-submit" disabled={authLoading}>
                {authLoading
                  ? 'Please wait...'
                  : mode === 'login'
                  ? `Log In & Accept Invite`
                  : `Sign Up & Accept Invite`}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitePage;
